import { useRef, useState } from 'react';
import type { RangeField } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { bt, btp } from '../../i18n';
import { commitTyped, isFree, trackSpan, typedSpan } from './rangeBounds';

/**
 * Slider + numeric readout (build-plan §6.3). Commits on input; the M3.4 history coalescer collapses a
 * drag burst into one undo entry (pointer-down→up window).
 *
 * ## The "same as desktop" state (18/09/2026)
 *
 * A slider that carries `inheritFrom` is the MOBILE TWIN of another one (see `RangeField.inheritFrom`),
 * and it has a third state the control set had no way to express: not a number, but "whatever the desktop
 * says". A number cannot mean that — every value in the range is a real answer — so the state is the
 * ABSENCE of a value, and this control has to make that absence visible, or the panel would show the
 * field's own default sitting under a picture painted at the desktop's value.
 *
 * The same shape serves a slider whose unset state is not a twin at all but a THEME value the component
 * computes ("Line height", 20/09/2026): `inheritLabel` with no `inheritFrom`, and the reset names the
 * automatic value instead of the desktop one.
 *
 * So: while the value is unset, the slider is replaced by a button naming what the phone is doing (and,
 * where the inherited number is literally the desktop's, showing it). Pressing it takes the value over,
 * starting EXACTLY where the desktop is, so nothing jumps under the seller's hand. Pressing the reset
 * beside the slider hands it back.
 *
 * ## The number beside the slider is the way OUT of the slider (20/09/2026)
 *
 * « on doit pas limiter le user à 24px […] même 100px s'il veut il peut la faire » (fondatrice). The
 * readout was a `<span>`, so a "Text size" that ends at 24 ENDED at 24. It is an input now: on a free
 * field (see `RangeField.bounds` / `rangeBounds.ts`) it takes whatever the seller types, and the track
 * stretches to hold it so the handle keeps meaning something and the drag can carry on from there.
 *
 * And the box alone was not enough (21/09): a readout-looking box invites nobody to type, and the
 * founder dragged to the end, read 24, and asked again. A free pixel track now starts at its unit's
 * reach — 100px — so the drag itself gets there (`trackSpan`); the box remains the way past that.
 * The same day, the other end: every heading size starts at 24px, and she asked to go under it. A free
 * pixel track now also starts at its unit's floor — 8px — on the schema's step grid (`trackSpan`).
 *
 * The track only ever GROWS while the panel is open. Typing 100 then dragging back to 20 must not
 * snap the track back to 10-24 mid-drag — the handle would jump to the far right under her hand, which
 * is the same lie the "same as desktop" state above exists to avoid.
 */
export function RangeControl({
  field,
  value,
  onChange,
  inherited,
}: {
  field: RangeField;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  /** What this field paints while it is unset — the sibling named by `inheritFrom`, resolved by the form. */
  inherited?: number | undefined;
}) {
  // `inheritLabel` alone (no sibling) is the same THIRD STATE without a desktop twin behind it: the
  // component computes the value when the setting is missing — the shared "Line height" slider is unset
  // until the seller takes the leading off the theme. Both spellings draw the same button.
  const inheritable = field.inheritFrom !== undefined || field.inheritLabel !== undefined;
  const fromDesktop = field.inheritFrom !== undefined;
  const isInherited = inheritable && value === undefined;
  const current = value ?? inherited ?? field.default ?? field.min;
  // `inheritLabel` is for the fields whose "unset" is NOT literally the desktop number (the video
  // section's mobile padding is 75 % of it) — there, showing the desktop's number would be a lie, so the
  // sentence stands alone. Everywhere else the inherited value is shown, because it is the truth.
  const inheritText = field.inheritLabel
    ? field.inheritLabel
    : `${bt('bld.range.same_as_desktop', 'Same as desktop')} · ${current}${field.unit ?? ''}`;
  // The reset hands the value BACK to whatever was deciding it. Naming the desktop on a field that has no
  // desktop twin would be a button that lies about what it does.
  const resetText = fromDesktop
    ? bt('bld.range.back_to_desktop', 'Back to the desktop value')
    : bt('bld.range.back_to_automatic', 'Back to the automatic value');

  // What the seller may TYPE, which on a free field is far past where the handle stops.
  const free = isFree(field);
  const span = typedSpan(field);
  // The handle's track. Held in a ref and only ever widened, for the reason in the header note; reset
  // when the row is reused for ANOTHER field (React keeps the instance when the panel swaps sections).
  const track = useRef({ id: field.id, ...trackSpan(field) });
  if (track.current.id !== field.id) {
    track.current = { id: field.id, ...trackSpan(field) };
  }
  track.current.min = Math.min(track.current.min, current);
  track.current.max = Math.max(track.current.max, current);

  // The half-typed state. `null` means "not typing" — the box then shows the committed value, so a drag
  // moves it. A draft is never parsed away under the seller: "1" on the way to "100" stays "1" on screen.
  const [draft, setDraft] = useState<string | null>(null);
  const typeAnyHint = free
    ? bt('bld.range.type_any', 'Type any value — the slider is only the usual range')
    : undefined;

  return (
    <FieldRow
      id={field.id}
      label={field.label}
      info={field.info}
      layout="row"
      labelAs={isInherited ? 'span' : 'label'}
    >
      <div className="bld-range">
        {isInherited ? (
          <button
            type="button"
            id={field.id}
            className="bld-range__inherit"
            onClick={() => onChange(current)}
          >
            {inheritText}
          </button>
        ) : (
          <>
            <input
              id={field.id}
              type="range"
              min={track.current.min}
              max={track.current.max}
              step={field.step ?? 1}
              value={current}
              onChange={(e) => {
                setDraft(null);
                onChange(Number(e.target.value));
              }}
            />
            <input
              className="bld-range__value"
              type="number"
              inputMode="decimal"
              aria-label={btp('bld.range.exact_value', '{label} — exact value', {
                label: field.label,
              })}
              title={typeAnyHint}
              min={span.min}
              max={span.max}
              step={field.step ?? 1}
              value={draft ?? String(current)}
              onChange={(e) => {
                setDraft(e.target.value);
                const n = Number(e.target.value);
                if (e.target.value !== '' && !Number.isNaN(n)) onChange(commitTyped(field, n));
              }}
              // Back to showing the committed value: whatever was refused (empty, "-", past the
              // ceiling) disappears instead of sitting there looking like it took.
              onBlur={() => setDraft(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setDraft(null);
              }}
            />
            {field.unit && <span className="bld-range__unit">{field.unit}</span>}
            {inheritable && (
              <button
                type="button"
                className="bld-range__reset"
                title={resetText}
                aria-label={resetText}
                onClick={() => onChange(undefined)}
              >
                ↺
              </button>
            )}
          </>
        )}
      </div>
    </FieldRow>
  );
}
